import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { postRequest } from "../../services/api";
import './LoginModel.css';

interface Props {
  show: boolean;
  onClose: () => void;
  onLoginSuccess: (user: any) => void;
  initialTab?: "login" | "register";
}

const emailRegex = /^\S+@\S+\.\S+$/;

const LoginModal: React.FC<Props> = ({ onClose, onLoginSuccess }) => {
  const [activeTab, setActiveTab] = useState<"login" | "register">("login");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [regPassword, setRegPassword] = useState("");

  // validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const navigate = useNavigate();

  //if (!show) return null;

  const closeAndReset = () => {
    setMessage(null);
    setErrors({});
    onClose();
  };

  const validateLogin = () => {
    const err: Record<string, string> = {};
    if (!loginEmail || !emailRegex.test(loginEmail)) err.email = "Please enter a valid email";
    if (!loginPassword || loginPassword.length < 4) err.password = "Password required (min 4 chars)";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateRegister = () => {
    const err: Record<string, string> = {};
    if (!firstName.trim()) err.firstname = "First name required";
    if (!lastName.trim()) err.lastname = "Last name required";
    if (!regEmail || !emailRegex.test(regEmail)) err.email = "Please enter a valid email";
    if (!mobile || !/^\d{10}$/.test(mobile)) err.mobile = "Enter a valid mobile number";
    if (!regPassword || regPassword.length < 4) err.password = "Password must be at least 4 characters";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setMessage(null);
    if (!validateLogin()) return;
    setLoading(true);
    try {
      const res = await postRequest("/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });
      const { token, user } = res.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      onLoginSuccess(user);
      setMessage("Login successful — redirecting...");
      closeAndReset();
      // Navigate to dashboard — keep existing behaviour
      const redirectPath = localStorage.getItem("postLoginModelRedirect") || "/dashboard";
      localStorage.removeItem("postLoginModelRedirect");
      navigate(redirectPath);
      window.location.reload();
    } catch (err: any) {
      setMessage(err?.response?.data?.message || "Login failed. Check credentials.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!validateRegister()) return;
    setLoading(true);
    try {
      // Call register API
      await postRequest("/auth/register", {
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        email: regEmail.trim(),
        mobile: mobile.trim(),
        password: regPassword,
      });

      // After successful registration — auto-login (so user need not login separately)
      const loginRes = await postRequest("/auth/login", {
        email: regEmail.trim(),
        password: regPassword,
      });

      const { token, user } = loginRes.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      onLoginSuccess(user);
      setMessage("Registration successful — logged in and redirecting...");
      closeAndReset();
      const redirectPath = localStorage.getItem("postLoginModelRedirect") || "/dashboard";
      localStorage.removeItem("postLoginModelRedirect");
      navigate(redirectPath);
      window.location.reload();
    } catch (err: any) {
      setMessage(err?.response?.data?.message || "Registration failed. Try again.");
      console.error("Register error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Allow clicking overlay to close
  const handleOverlayClick = (ev: React.MouseEvent) => {
    if ((ev.target as HTMLElement).classList.contains("lr-modal-overlay")) {
      closeAndReset();
    }
  };

  return (
    <div className="lr-modal-overlay" onMouseDown={handleOverlayClick}>
      <div className="lr-modal">
        <button className="lr-close" onClick={closeAndReset} aria-label="Close modal">✕</button>

        <div className="lr-tabs">
          <button
            className={`lr-tab ${activeTab === "login" ? "active" : ""}`}
            onClick={() => { setActiveTab("login"); setMessage(null); setErrors({}); }}
          >
            Login
          </button>
          <button
            className={`lr-tab ${activeTab === "register" ? "active" : ""}`}
            onClick={() => { setActiveTab("register"); setMessage(null); setErrors({}); }}
          >
            Register
          </button>
        </div>

        <div className="lr-body">
          <div className="lr-left">
            {activeTab === "login" ? (
              <form className="lr-form" onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="sr-only">Email</label>
                  <input
                    type="email"
                    placeholder="Email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label className="sr-only">Password</label>
                  <input
                    type="password"
                    placeholder="Password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>

                <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                  {loading ? "Signing in..." : "Login"}
                </button>
              </form>
            ) : (
              <form className="lr-form" onSubmit={handleRegister}>
                <div className="row">
                  <div className="col-6 mb-2">
                    <input
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={`form-control ${errors.firstname ? "is-invalid" : ""}`}
                    />
                    {errors.firstname && <div className="invalid-feedback">{errors.firstname}</div>}
                  </div>
                  <div className="col-6 mb-2">
                    <input
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={`form-control ${errors.lastname ? "is-invalid" : ""}`}
                    />
                    {errors.lastname && <div className="invalid-feedback">{errors.lastname}</div>}
                  </div>
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <input
                    type="tel"
                    placeholder="Mobile (digits only)"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                  />
                  {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>

                <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                  {loading ? "Creating account..." : "Register"}
                </button>
              </form>
            )}

            {message && <div className="lr-message mt-2">{message}</div>}
          </div>

          <div className="lr-right">
            <h2 className="lr-title">{activeTab === "login" ? "Welcome Back!" : "Join Acadebility"}</h2>
            <p className="lr-sub">
              {activeTab === "login"
                ? "Login to access your dashboard, test history and personalized content."
                : "Register to unlock tests, track progress and access premium resources."}
            </p>

            <ul className="lr-features">
              <li>✔ Access tests & solutions</li>
              <li>✔ Track your progress</li>
              <li>✔ Save favourite posts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
